import { useEffect, useState, CSSProperties } from 'react';
import { DiagnosticsReport, DiagnosticsTestView } from '../types/database';
import { PCAgentClient } from '../services/pcAgent';

interface DiagnosticsReportModalProps {
  /** e.g. "CLEOPATRA-03" — which PC the report belongs to. */
  contextLine: string;
  /** Agent client to fetch GET /api/diagnostics/run from. */
  client: PCAgentClient;
  onClose: () => void;
}

// Status → icon/color for a test row. Unknown statuses fall back to a neutral dot.
const statusIcon = (status: string): { icon: string; color: string } => {
  switch (status) {
    case 'pass':
      return { icon: '✓', color: '#1d7a46' };
    case 'fail':
      return { icon: '✕', color: '#b3261e' };
    case 'running':
      return { icon: '…', color: '#2B6AC0' };
    case 'skipped':
    case 'pending':
      return { icon: '—', color: '#8a97a6' };
    default:
      return { icon: '·', color: '#8a97a6' };
  }
};

// "convai_liveness" → "convai liveness"
const humanizeTestId = (id: string): string => id.replace(/_/g, ' ');

/**
 * Read-only drawer/modal rendering the full diagnostics run report (per-test
 * rows with status icons, severity, messages, failure codes). Fetches on
 * mount; the parent only mounts it while open. Styling clones MarkIssueModal
 * (fixed backdrop + centered white card).
 */
export default function DiagnosticsReportModal({ contextLine, client, onClose }: DiagnosticsReportModalProps) {
  const [report, setReport] = useState<DiagnosticsReport | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    client.getDiagnosticsReport().then((data) => {
      if (cancelled) return;
      setReport(data);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [client]);

  const metaChipStyle: CSSProperties = {
    fontSize: 12,
    fontWeight: 600,
    padding: '3px 8px',
    borderRadius: 100,
    background: '#eef2f7',
    color: '#54626f',
    whiteSpace: 'nowrap',
  };

  const renderTestRow = (test: DiagnosticsTestView) => {
    const { icon, color } = statusIcon(test.status);
    return (
      <div
        key={test.id}
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 10,
          padding: '8px 4px',
          borderBottom: '1px solid #eef2f7',
          fontSize: 13,
        }}
      >
        <span style={{ color, fontWeight: 700, width: 16, textAlign: 'center', flexShrink: 0 }}>{icon}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600 }}>{humanizeTestId(test.id)}</span>
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: test.severity === 'blocking' ? '#b3261e' : '#8a97a6',
              }}
            >
              {test.severity}
            </span>
            {test.duration_ms > 0 && (
              <span style={{ fontSize: 11, color: '#8a97a6' }}>{test.duration_ms} ms</span>
            )}
          </div>
          {test.message && <div style={{ color: '#54626f', marginTop: 2 }}>{test.message}</div>}
          {test.failure_code && (
            <div style={{ color: '#b3261e', marginTop: 2, fontFamily: 'monospace', fontSize: 12 }}>
              {test.failure_code}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(16,32,46,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 14,
          padding: 24,
          width: 'min(560px, calc(100vw - 32px))',
          maxHeight: 'calc(100vh - 64px)',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 12px 40px rgba(0,0,0,0.3)',
          color: '#16202e',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ margin: 0 }}>Diagnostics report</h3>
        <div style={{ fontSize: 13, color: '#54626f', marginTop: 4 }}>{contextLine}</div>

        {loading && (
          <div style={{ padding: '24px 0', color: '#8a97a6', fontSize: 13 }}>Loading report…</div>
        )}
        {!loading && !report && (
          <div style={{ padding: '24px 0', color: '#b3261e', fontSize: 13 }}>
            Could not load the diagnostics report from the agent.
          </div>
        )}

        {!loading && report && (
          <>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12 }}>
              <span style={metaChipStyle}>state: {report.state}</span>
              <span style={metaChipStyle}>enforcement: {report.enforcement}</span>
              {report.trigger && <span style={metaChipStyle}>trigger: {report.trigger}</span>}
              <span style={metaChipStyle}>reboots 24h: {report.reboots_24h}</span>
            </div>

            {report.override?.active && (
              <div
                style={{
                  marginTop: 10,
                  padding: '8px 10px',
                  borderRadius: 8,
                  border: '1px solid rgba(220, 38, 38, 0.35)',
                  background: 'rgba(220, 38, 38, 0.08)',
                  color: '#b3261e',
                  fontSize: 13,
                }}
              >
                Overridden by {report.override.by || 'unknown'}
                {report.override.at ? ` at ${new Date(report.override.at).toLocaleString()}` : ''}
                {report.override.reason ? ` — ${report.override.reason}` : ''}
              </div>
            )}

            {(report.pending_hmd?.length ?? 0) > 0 && (
              <div style={{ marginTop: 10, fontSize: 13, color: '#54626f' }}>
                Pending HMD tests (run on headset connect): {report.pending_hmd!.map(humanizeTestId).join(', ')}
              </div>
            )}

            <div style={{ marginTop: 12, overflowY: 'auto', flex: 1, minHeight: 0 }}>
              {(report.tests || []).length === 0 ? (
                <div style={{ color: '#8a97a6', fontSize: 13, padding: '12px 0' }}>No test results yet.</div>
              ) : (
                report.tests.map(renderTestRow)
              )}
            </div>

            {report.last_result && (
              <div style={{ marginTop: 10, fontSize: 12, color: '#8a97a6' }}>
                Last result: {report.last_result.status}
                {report.last_result.finished_at
                  ? ` — finished ${new Date(report.last_result.finished_at).toLocaleString()}`
                  : ''}
              </div>
            )}
          </>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <button className="refresh-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
